import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button, Modal, TextInput, Typography } from "@/components/atoms";
import { SubscriptionPlan } from "@/redux/apis/subscription/types";

interface ViewDemoModalProps {
  open: boolean;
  onClose: () => void;
  plan?: SubscriptionPlan;
}

const schema = z.object({
  fullName: z.string().min(1, { message: "Field is required and can't be empty." }).max(100, "Maximum of 100 characters."),
  emailAddress: z
    .string()
    .min(1, { message: "Field is required and can't be empty." })
    .email("Email address is not valid")
    .max(100, "Maximum of 100 characters."),
  mobileNumber: z.string().min(1, { message: "Field is required and can't be empty." }),
});

type DemoFormData = z.infer<typeof schema>;

export const ViewDemoModal = ({ open, onClose, plan }: ViewDemoModalProps) => {
  const planName = plan?.subscriptionName || "Plan";
  const iconSvg = plan?.iconSvg;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<DemoFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      fullName: "",
      emailAddress: "",
      mobileNumber: "",
    },
  });

  const onSubmit = (_data: DemoFormData) => {
    // TODO: Call API to send demo request
    reset();
    onClose();
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  const renderForm = () => (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6 text-neutral-dark-gray">
      {/* Full Name Field */}
      <div className="flex flex-col gap-1">
        <Typography variant="action">Full Name</Typography>
        <TextInput
          size="md"
          className="w-full"
          type="text"
          hasError={!!errors.fullName}
          placeholder="Enter Full Name"
          {...register("fullName")}
        />
        {errors.fullName && (
          <Typography variant="paragraph2" className="text-danger">
            {errors.fullName.message}
          </Typography>
        )}
      </div>

      {/* Email Address Field */}
      <div className="flex flex-col gap-1">
        <Typography variant="action">Email Address</Typography>
        <TextInput
          size="md"
          className="w-full"
          type="email"
          hasError={!!errors.emailAddress}
          placeholder="Enter email address"
          {...register("emailAddress")}
        />
        {errors.emailAddress && (
          <Typography variant="paragraph2" className="text-danger">
            {errors.emailAddress.message}
          </Typography>
        )}
      </div>

      {/* Mobile Number Field */}
      <div className="flex flex-col gap-1">
        <Typography variant="action">Mobile Number</Typography>
        <TextInput
          size="md"
          className="w-full"
          type="tel"
          hasError={!!errors.mobileNumber}
          placeholder="000 000 000"
          {...register("mobileNumber")}
        />
        {errors.mobileNumber && (
          <Typography variant="paragraph2" className="text-danger">
            {errors.mobileNumber.message}
          </Typography>
        )}
      </div>

      {/* Action Buttons */}
      <div className="mt-2 flex flex-row justify-end gap-3">
        <Button type="button" size="lg" variant="secondary" onClick={handleCancel}>
          Cancel
        </Button>
        <Button type="submit" size="lg" variant="primary">
          Send Demo
        </Button>
      </div>
    </form>
  );

  return (
    <Modal open={open} onClose={onClose} className="w-[85vw] md:w-[450px] lg:w-[450px]">
      <div className="flex flex-col gap-6">
        {/* Plan Header */}
        <div className="flex flex-row items-center gap-4 rounded-lg bg-neutral-surface-gray p-5 text-neutral-dark-gray">
          {iconSvg && (
            <div className="flex h-12 w-12 flex-shrink-0 [&>svg]:h-full [&>svg]:w-full" dangerouslySetInnerHTML={{ __html: iconSvg }} />
          )}
          <div className="flex flex-col">
            <Typography variant="h2">{planName}</Typography>
            <Typography variant="paragraph">View Demo</Typography>
          </div>
        </div>

        {/* Description */}
        <div className="flex flex-col gap-1 text-neutral-dark-gray">
          <Typography variant="title">Get the {planName} Operator Plan Demo Video</Typography>
          <Typography variant="paragraph">Enter your details and we&apos;ll send you the demo video link for the plan</Typography>
        </div>

        {renderForm()}
      </div>
    </Modal>
  );
};
